// src/pages/MyPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import styles from './MyPage.module.css';

export default function MyPage() {
  const navigate = useNavigate();
  
  // 사용자 정보
  const [userInfo, setUserInfo] = useState(null);
  
  // 휴가 잔여 시간
  const [leaveBalance, setLeaveBalance] = useState({
    annual_leave_hours: 0,
    alt_leave_hours: 0,
    special_leave_hours: 0,
  });

  // 내가 작성한 캘린더 일정
  const [myCalendarEvents, setMyCalendarEvents] = useState([]);

  // 비밀번호 변경
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // 데이터 불러오기
  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // 사용자 정보 가져오기
      const userRes = await api.get('/mypage/');
      setUserInfo(userRes.data);
      
      // 휴가 잔여 시간 가져오기
      const leaveRes = await api.get('/mypage/leave_balance/');
      setLeaveBalance(leaveRes.data);
      
      // 내가 작성한 캘린더 일정 가져오기
      const eventsRes = await api.get('/mypage/my_calendar_events/');
      setMyCalendarEvents(eventsRes.data || []);
      
      setLoading(false);
    } catch (err) {
      console.error('데이터 불러오기 실패:', err);
      setError('데이터를 불러오는데 실패했습니다.');
      setLoading(false);
    }
  };

  // 비밀번호 변경 처리
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    try {
      const response = await api.post('/mypage/change_password/', passwordForm);
      
      setSuccessMsg(response.data.message || '비밀번호가 변경되었습니다.');
      
      // 폼 초기화
      setPasswordForm({
        old_password: '',
        new_password: '',
        new_password_confirm: '',
      });

      // 3초 후 로그인 페이지로 이동
      setTimeout(() => {
        alert('비밀번호가 변경되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
      }, 2000);
      
    } catch (err) {
      console.error('비밀번호 변경 실패:', err);
      
      if (err.response?.data) {
        const errors = err.response.data;
        
        if (errors.old_password) {
          setError(errors.old_password[0] || '현재 비밀번호가 일치하지 않습니다.');
        } else if (errors.new_password) {
          setError(errors.new_password[0] || '새 비밀번호가 유효하지 않습니다.');
        } else if (errors.new_password_confirm) {
          setError(errors.new_password_confirm[0] || '새 비밀번호가 일치하지 않습니다.');
        } else {
          setError('비밀번호 변경에 실패했습니다.');
        }
      } else {
        setError('비밀번호 변경에 실패했습니다.');
      }
    }
  };

  // 사용 시간 계산 함수 (휴게시간 반영)
  const calculateDuration = (startTime, endTime, breakMinutes = 0) => {
    if (!startTime || !endTime) return '-';
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    let durationMinutes = endMinutes - startMinutes;
    
    // 휴게시간 차감
    durationMinutes -= breakMinutes;
    
    // 음수 방지
    if (durationMinutes < 0) durationMinutes = 0;
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}시간 ${minutes}분`;
    } else if (hours > 0) {
      return `${hours}시간`;
    } else {
      return `${minutes}분`;
    }
  };

  // ✅ 휴게시간 포맷팅 함수 (break_hours를 break_minutes로 변환)
  const formatBreakTime = (breakHours) => {
    if (!breakHours || breakHours === 0) return '-';
    
    const breakMinutes = breakHours * 60; // ✅ 시간을 분으로 변환
    const hours = Math.floor(breakMinutes / 60);
    const minutes = breakMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}시간 ${minutes}분`;
    } else if (hours > 0) {
      return `${hours}시간`;
    } else {
      return `${minutes}분`;
    }
  };
  
  // 날짜 포맷팅 함수
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>로딩 중...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>마이페이지</h1>
      </div>

      {/* 사용자 정보 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>내 정보</h2>
        <div className={styles.infoCard}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>아이디:</span>
            <span className={styles.infoValue}>{userInfo?.username}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>이름:</span>
            <span className={styles.infoValue}>
              {userInfo?.name || userInfo?.display_name || '미등록'}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>직위:</span>
            <span className={styles.infoValue}>
              {userInfo?.position || '미등록'}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>이메일:</span>
            <span className={styles.infoValue}>{userInfo?.email || '미등록'}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>휴대전화:</span>
            <span className={styles.infoValue}>{userInfo?.mobile || '미등록'}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>직통전화:</span>
            <span className={styles.infoValue}>{userInfo?.direct_phone || '미등록'}</span>
          </div>
        </div>
      </section>

      {/* 휴가 잔여 시간 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>휴가 잔여 시간</h2>
        <div className={styles.leaveCard}>
          <div className={styles.leaveItem}>
            <div className={styles.leaveIcon} style={{ backgroundColor: '#fff9c4' }}>
              📅
            </div>
            <div className={styles.leaveInfo}>
              <div className={styles.leaveLabel}>연차 / 반차</div>
              <div className={styles.leaveValue}>
                {Number(leaveBalance.annual_leave_hours).toFixed(2)} 시간
              </div>
            </div>
          </div>

          <div className={styles.leaveItem}>
            <div className={styles.leaveIcon} style={{ backgroundColor: '#f8bbd0' }}>
              🔄
            </div>
            <div className={styles.leaveInfo}>
              <div className={styles.leaveLabel}>대체휴무</div>
              <div className={styles.leaveValue}>
                {Number(leaveBalance.alt_leave_hours).toFixed(2)} 시간
              </div>
            </div>
          </div>

          <div className={styles.leaveItem}>
            <div className={styles.leaveIcon} style={{ backgroundColor: '#ffe0b2' }}>
              ⭐
            </div>
            <div className={styles.leaveInfo}>
              <div className={styles.leaveLabel}>특별휴가</div>
              <div className={styles.leaveValue}>
                {Number(leaveBalance.special_leave_hours).toFixed(2)} 시간
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 내가 작성한 캘린더 일정 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>내 일정 내역</h2>
        <div className={styles.eventsContainer}>
          {(() => {
            // 담당자가 본인인 일정만 필터링
            const filteredEvents = myCalendarEvents.filter((event) => {
              return event.assignee === userInfo?.id || 
                     event.assignee_display === userInfo?.name ||
                     event.assignee_display === userInfo?.display_name;
            });

            if (filteredEvents.length === 0) {
              return (
                <div className={styles.noEvents}>
                  <p>작성한 일정이 없습니다.</p>
                </div>
              );
            }

            return (
              <div className={styles.eventsTable}>
                <table>
                  <thead>
                    <tr>
                      <th>작성일</th>
                      <th>일정 날짜</th>
                      <th>시간</th>
                      <th>사용시간</th>
                      <th>휴게시간</th>
                      <th>담당자</th>
                      <th>제목</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((event) => (
                      <tr key={event.id}>
                        <td>{formatDate(event.created_at)}</td>
                        <td>{formatDate(event.start_date)}</td>
                        <td>
                          {event.start_time && event.end_time
                            ? `${event.start_time.slice(0, 5)} ~ ${event.end_time.slice(0, 5)}`
                            : '종일'}
                        </td>
                        
                        {/* ✅ break_hours를 분으로 변환해서 전달 */}
                        <td>{calculateDuration(event.start_time, event.end_time, (event.break_hours || 0) * 60)}</td>
                        
                        {/* ✅ break_hours를 formatBreakTime에 전달 */}
                        <td>{formatBreakTime(event.break_hours)}</td>
                        
                        <td>{event.assignee_display || '미지정'}</td>
                        <td className={styles.titleCell}>
                          <span className={styles.eventType}>{event.event_type}</span>
                          {event.title && ` - ${event.title}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            );
          })()}
        </div>
      </section>

      {/* 비밀번호 변경 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>비밀번호 변경</h2>
        <form onSubmit={handlePasswordChange} className={styles.passwordForm}>
          {error && <div className={styles.errorMsg}>{error}</div>}
          {successMsg && <div className={styles.successMsg}>{successMsg}</div>}

          <div className={styles.formGroup}>
            <label htmlFor="old_password" className={styles.formLabel}>
              현재 비밀번호
            </label>
            <input
              type="password"
              id="old_password"
              className={styles.formInput}
              value={passwordForm.old_password}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, old_password: e.target.value })
              }
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="new_password" className={styles.formLabel}>
              새 비밀번호
            </label>
            <input
              type="password"
              id="new_password"
              className={styles.formInput}
              value={passwordForm.new_password}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, new_password: e.target.value })
              }
              required
            />
            <small className={styles.formHint}>
              8자 이상, 숫자와 문자를 포함해주세요
            </small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="new_password_confirm" className={styles.formLabel}>
              새 비밀번호 확인
            </label>
            <input
              type="password"
              id="new_password_confirm"
              className={styles.formInput}
              value={passwordForm.new_password_confirm}
              onChange={(e) =>
                setPasswordForm({
                  ...passwordForm,
                  new_password_confirm: e.target.value,
                })
              }
              required
            />
          </div>

          <button type="submit" className={styles.submitBtn}>
            비밀번호 변경
          </button>
        </form>
      </section>
    </div>
  );
}